// src/components/molecules/FormFields/FundingGoalInput.js

import React, { useState } from "react";
import { Typography, Box, Grid } from "@mui/material";
import { useTheme } from "styled-components";

const FundingGoalInput = ({ challenge, value, onChange, error, disabled }) => {
  const theme = useTheme();
  const [fundingGoal, setFundingGoal] = useState(value.fundingGoal || "");
  const [digitalSupply, setDigitalSupply] = useState(
    typeof value.digitalSupply === "number" ? value.digitalSupply : 1
  );

  // Handler for funding goal input
  const handleFundingGoalChange = (event) => {
    const { value } = event.target;
    setFundingGoal(value);
    onChange({ target: { name: "fundingGoal", value } });
  };

  // Handler for digital supply input, parsing value to number
  const handleDigitalSupplyChange = (event) => {
    const { value } = event.target;
    setDigitalSupply(value);
    onChange({ target: { name: "digitalSupply", value } });
  };

  // Handler for rounding funding goal to the nearest multiple of 5
  const handleFundingGoalBlur = () => {
    let parsedValue = parseFloat(fundingGoal);
    if (isNaN(parsedValue) || parsedValue <= 0) {
      parsedValue = 5;
    } else if (parsedValue > 1250000) {
      parsedValue = 1250000;
    } else {
      parsedValue = Math.round(parsedValue / 5) * 5;
    }
    setFundingGoal(parsedValue);
    onChange({ target: { name: "fundingGoal", value: parsedValue } });
  };

  // Handler for enforcing a minimum value of 1 and a maximum value of 2000
  const handleDigitalSupplyBlur = () => {
    let parsedValue = parseFloat(digitalSupply); // Use digitalSupply instead of fundingGoal
    if (isNaN(parsedValue) || parsedValue <= 0) {
      parsedValue = 1; // Default to 1 if the value is invalid or <= 0
    } else if (parsedValue > 2000) {
      parsedValue = 2000; // Cap the maximum at 2000
    }
    setDigitalSupply(parsedValue);
    onChange({ target: { name: "digitalSupply", value: parsedValue } });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2}>
        {/* Funding Goal Input */}
        <Grid item xs={12} md={3}>
          <Typography
            variant="body1"
            component="label"
            htmlFor="fundingGoal"
            sx={{ fontFamily: "basic-sans", color: "white" }}
          >
            {challenge.allowCrowdFunding
              ? "Set Funding Goal ($)"
              : "Set Purchase Price ($)"}
          </Typography>
          <input
            type="text"
            id="fundingGoal"
            name="fundingGoal"
            value={fundingGoal}
            onChange={handleFundingGoalChange}
            onBlur={handleFundingGoalBlur}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
            style={{
              width: "100%",
              padding: "10px",
              marginTop: "8px",
              fontFamily: "basic-sans",
              backgroundColor: disabled ? "#f0f0f0" : "white",
              borderRadius: "5px",
              border: error
                ? `1px solid ${theme.colors.red}`
                : "1px solid #ccc",
              color: disabled ? theme.colors.gray : "black",
            }}
          />
          {error && (
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.red,
                mt: 1,
                fontFamily: "basic-sans",
              }}
            >
              {error}
            </Typography>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={9}
          sx={{
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontFamily: "basic-sans",
              color: theme.colors.darkGray,
              fontSize: "0.9rem",
            }}
          >
            {challenge.allowCrowdFunding
              ? "Set your fundraising goal (up to $1,250,000 USD). You'll receive contributions even if the goal isn't met, with a minimum of $5. A fee applies."
              : "Enable others to purchase and own this content. Set a fair price (minimum $5) to attract buyers while ensuring value for your content."}
          </Typography>
        </Grid>

        {/* Digital Supply Input - Only show if allowCrowdFunding is false */}
        {!challenge.allowCrowdFunding && (
          <>
            <Grid item xs={12} md={3}>
              <Typography
                variant="body1"
                component="label"
                htmlFor="digitalSupply"
                sx={{ fontFamily: "basic-sans", color: "white" }}
              >
                Set Digital Supply
              </Typography>
              <input
                type="text"
                id="digitalSupply"
                name="digitalSupply"
                value={digitalSupply}
                onChange={handleDigitalSupplyChange}
                onBlur={handleDigitalSupplyBlur}
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={disabled}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "8px",
                  fontFamily: "basic-sans",
                  backgroundColor: disabled ? "#f0f0f0" : "white",
                  borderRadius: "5px",
                  border: error
                    ? `1px solid ${theme.colors.red}`
                    : "1px solid #ccc",
                  color: disabled ? theme.colors.gray : "black",
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={9}
              sx={{
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "basic-sans",
                  color: theme.colors.darkGray,
                  fontSize: "0.9rem",
                }}
              >
                Set the total number (minimum 1, max 2,000) of digital items available for purchase.
                For example, 1,000 items at $10 each creates a total value of
                $10,000.
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default FundingGoalInput;
